import { Box, TableCell, TableRow } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useState } from "react";
import { FormProvider, useForm, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { parseISO } from "date-fns";
import { useAppDispatch } from "../../../../../app/hooks";
import DateField from "../../../../../common/components/functional/dateField/DateField";
import { UnsavedChangesProvider } from "../../../../../contexts/UnsavedChangesContext";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import type { Employment } from "../../../../employment/employmentApi";
import {
  addPeriodStartSetting,
  editPeriodStartSetting,
} from "../../../../employment/employmentSlice";
import {
  PeriodStartFormValues,
  PeriodStartRow,
  periodStartValidationErrors,
  toPeriodStartForm,
  toPeriodStartSetting,
} from "./helpers";

import { PeriodStartActionButton, PeriodStartActions } from "./styles";

export type PeriodStartDraft = { employment: Employment; row?: PeriodStartRow };

type Props = {
  draft: PeriodStartDraft;
  canEdit: boolean;
  onClose: () => void;
  onError: (code?: string) => void;
  onDelete?: () => void;
  isDeleting?: boolean;
};

export const PeriodStartSettingsEditor = ({
  draft,
  canEdit,
  onClose,
  onError,
  onDelete,
  isDeleting = false,
}: Props) => {
  const { t } = useTranslation("talentProfile");
  const dispatch = useAppDispatch();
  // Keep the opening version and original date; background reads must not change the edit's target.
  const [{ employment, row }] = useState(draft);
  const originalFrom = row && !row.isBaseline ? row.setting.from : undefined;
  const methods = useForm<PeriodStartFormValues>({
    defaultValues: toPeriodStartForm(row?.setting),
    mode: "onChange",
  });
  const values = useWatch({ control: methods.control });
  const { isDirty, isSubmitting, touchedFields, submitCount, errors } =
    useFormState({ control: methods.control });
  const validation = periodStartValidationErrors(
    values as PeriodStartFormValues,
    employment.details,
    originalFrom,
  );
  const fixedFrom = row?.setting.from === employment.details.agreedStart;
  const disabled = !canEdit || isSubmitting || isDeleting;
  const save = methods.handleSubmit(async (data) => {
    if (
      !canEdit ||
      Object.keys(
        periodStartValidationErrors(data, employment.details, originalFrom),
      ).length
    )
      return;
    const body = {
      employmentId: employment.employmentId,
      version: employment.details.version,
      setting: toPeriodStartSetting(data),
    };
    const result = originalFrom
      ? await dispatch(editPeriodStartSetting({ ...body, from: originalFrom }))
      : await dispatch(addPeriodStartSetting(body));
    if (
      addPeriodStartSetting.fulfilled.match(result) ||
      editPeriodStartSetting.fulfilled.match(result)
    )
      onClose();
    else onError(result.error.code);
  });
  // New empty rows are normal input, not validation failures. Reveal each error after blur or submit.
  const fromError =
    touchedFields.from || submitCount > 0 ? validation.from : undefined;
  const anchorError =
    touchedFields.periodStart || submitCount > 0
      ? validation.periodStart
      : undefined;

  return (
    <FormProvider {...methods}>
      <UnsavedChangesProvider isDirty={isDirty}>
        <TableRow>
          <TableCell sx={{ verticalAlign: "top" }}>
            <DateField
              name="from"
              label={t("periodStartEffectiveFrom")}
              required={{ value: true, message: t("common:invalidDate") }}
              disabled={disabled}
              readOnly={fixedFrom}
              minDate={parseISO(employment.details.agreedStart)}
              maxDate={
                employment.details.agreedEnd
                  ? parseISO(employment.details.agreedEnd)
                  : undefined
              }
              error={!!fromError}
              showHelperText={false}
            />
            {fromError && (
              <Box
                role="alert"
                sx={{ color: "error.main", fontSize: 12, mt: 0.5 }}
              >
                {t(fromError)}
              </Box>
            )}
          </TableCell>
          <TableCell sx={{ verticalAlign: "top" }}>
            <DateField
              name="periodStart"
              label={t("periodStarts")}
              required={{ value: true, message: t("common:invalidDate") }}
              disabled={disabled}
              maxDate={values.from ?? undefined}
              error={!!anchorError}
              showHelperText={false}
            />
            {anchorError && (
              <Box
                role="alert"
                sx={{ color: "error.main", fontSize: 12, mt: 0.5 }}
              >
                {t(anchorError)}
              </Box>
            )}
          </TableCell>
          <TableCell sx={{ verticalAlign: "top" }}>
            <PeriodStartActions>
              <PeriodStartActionButton
                color="success"
                aria-label={t("common:save")}
                onClick={() => save()}
                disabled={
                  disabled ||
                  !isDirty ||
                  Object.keys(validation).length > 0 ||
                  Object.keys(errors).length > 0
                }
              >
                <CheckIcon />
              </PeriodStartActionButton>
              <PeriodStartActionButton
                color="error"
                aria-label={t("common:cancel")}
                onClick={onClose}
                disabled={isSubmitting || isDeleting}
              >
                <CloseIcon />
              </PeriodStartActionButton>
              {onDelete && row && !row.isBaseline && (
                <PeriodStartActionButton
                  aria-label={t("common:delete")}
                  onClick={onDelete}
                  disabled={disabled}
                  color="error"
                  sx={{ ml: 1 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </PeriodStartActionButton>
              )}
            </PeriodStartActions>
          </TableCell>
        </TableRow>
      </UnsavedChangesProvider>
    </FormProvider>
  );
};
